// Copy en español rioplatense. Fuente única de texto.
// CLAUDE.md invariante 5: nunca texto literal en JSX, siempre a través de strings.*

export const strings = {
  brand: {
    name: "Profind",
    tagline: "Encontrá tu profesional en Argentina",
  },
  nav: {
    whatIs: "¿Qué es?",
    howItWorks: "Cómo funciona",
    forProfessionals: "Para profesionales",
    faq: "FAQ",
    downloadCta: "Descargá la app",
  },
  hero: {
    eyebrow: "Profesionales verificados en Argentina",
    title: "Encontrá al profesional indicado, cerca tuyo.",
    subtitle:
      "Buscá por rubro, zona y modalidad. Contactá directo. Gratis siempre para vos.",
    ctaPrimary: "Descargar la app",
    ctaSecondary: "¿Sos profesional? Sumate acá",
    values: [
      { label: "Confianza", tone: "blue" },
      { label: "Cercanía", tone: "jade" },
      { label: "Visibilidad", tone: "amber" },
      { label: "Privacidad", tone: "violet" },
    ],
    visualAlt: "Vista previa de la app Profind en un teléfono",
    mockup: {
      searchTitle: "Cerca tuyo",
      searchPlaceholder: "Especialidad, zona o nombre",
      filterAll: "Todos",
      filterOnline: "Online",
      filterPresencial: "Presencial",
      available: "Disponible",
      profileBadge: "Verificado",
      profileBio:
        "Acompaño procesos de ansiedad y autoestima desde un enfoque integrador.",
      profileChips: ["Ansiedad", "Adultos", "Online"],
      profileCta: "Contactar por WhatsApp",
      pros: [
        {
          name: "Profesional Uno",
          spec: "Psicología clínica",
          location: "Luján de Cuyo",
          distance: "1,1 km",
          initials: "PU",
          tags: ["Ansiedad", "Vínculos", "Adolescencia"],
        },
        {
          name: "Profesional Dos",
          spec: "Psicología Cognitivo-Conductual",
          location: "Godoy Cruz",
          distance: "2,2 km",
          initials: "PD",
          tags: ["Ansiedad", "Depresión", "Terapia de pareja"],
        },
        {
          name: "Profesional Tres",
          spec: "Psicoanálisis",
          location: "Guaymallén",
          distance: "3,3 km",
          initials: "PT",
          tags: ["Pareja", "Autoestima", "Duelo"],
        },
      ],
    },
  },
  howItWorks: {
    eyebrow: "Para personas que buscan un profesional",
    title: "Cómo funciona",
    subtitle:
      "Tres pasos para encontrar al profesional adecuado. Sin formularios intermediarios, sin esperas.",
    mockSearchTitle: "A tu medida",
    searchFilters: ["Ansiedad", "Online", "Mujer", "OSDE"],
    contactChannels: [
      { kind: "whatsapp" as const, label: "WhatsApp", value: "Mensaje directo" },
      { kind: "instagram" as const, label: "Instagram", value: "@profesionaluno" },
      { kind: "linkedin" as const, label: "LinkedIn", value: "Ver perfil" },
      { kind: "x" as const, label: "X", value: "@profesionaluno" },
      { kind: "web" as const, label: "Sitio web", value: "profesionaluno.com.ar" },
    ],
    steps: [
      {
        number: "01",
        title: "Buscás a tu medida",
        body: "La app te muestra profesionales filtrados por lo que importa: especialidad, modalidad presencial u online, cercanía, género u obra social. Vos definís qué pesa más en tu búsqueda.",
        mockupAlt: "Listado de profesionales filtrados por las preferencias del usuario",
      },
      {
        number: "02",
        title: "Elegís con información",
        body: "Cada perfil muestra formación, línea teórica, áreas de trabajo y un texto del profesional. Sabés con quién vas a hablar antes del primer contacto.",
        mockupAlt: "Perfil de un profesional con sus áreas y descripción",
      },
      {
        number: "03",
        title: "Contactás como prefieras",
        body: "Coordinás directamente con el profesional por el canal que ofrezca: WhatsApp, Instagram, LinkedIn, X o su sitio web. Sin formularios intermediarios, sin esperas.",
        mockupAlt: "Canales de contacto que ofrece el profesional",
      },
    ],
  },
  whatIs: {
    eyebrow: "Qué es Profind",
    title: "Una forma distinta de encontrar tu profesional",
    subtitle:
      "Buscar profesional no debería ser una búsqueda a ciegas. Por eso construimos algo distinto.",
    cards: [
      {
        tone: "neutral",
        title: "El problema",
        body: "Encontrar al profesional adecuado es una búsqueda fragmentada. Recomendaciones aisladas, perfiles sin información real, redes sociales que no fueron diseñadas para esto.",
      },
      {
        tone: "primary",
        title: "La solución",
        body: "Una plataforma donde cada profesional muestra su línea teórica, especialización, modalidad y zona. Vos elegís con criterio, no por azar.",
      },
      {
        tone: "accent",
        title: "La diferencia",
        body: "Profesionales verificados con matrícula vigente. Búsqueda por proximidad real. Contacto directo, sin intermediarios ni comisiones.",
      },
    ],
  },
  forProfessionals: {
    eyebrow: "Para profesionales",
    title: "¿Sos profesional? Llegá a más personas.",
    subtitle:
      "Mostrá quién sos profesionalmente, llegá a personas que buscan exactamente lo que ofrecés.",
    benefits: [
      {
        title: "Visibilidad real",
        body: "Tu perfil aparece cuando alguien busca o explora lo que ofrecés.",
      },
      {
        title: "Diferenciación",
        body: "Mostrá tu línea teórica, tu estilo, tus áreas. No sos un nombre más en una lista.",
      },
      {
        title: "Contacto directo",
        body: "Los clientes te escriben directo por los canales que vos elijas. Vos manejás tus reglas — la plataforma no se mete.",
      },
    ],
    pricing: {
      label: "Plan base profesional",
      amount: "Gratis",
      period: "",
      promo: "Siempre visible, con tu panel de visitas y clics incluido.",
      guarantee: "Hoy el plan base es sin costo. Más adelante habrá un plan premium opcional.",
      cta: "Sumate a la lista de espera",
    },
  },
  trust: {
    eyebrow: "Marco institucional",
    title: "Confianza y verificación",
    subtitle:
      "Tres compromisos que sostienen la plataforma. Cómo cuidamos a quienes la usan.",
    pillars: [
      {
        key: "verification" as const,
        title: "Verificación profesional",
        body: "Todos los profesionales son verificados con matrícula vigente antes de aparecer en la plataforma. Validamos manualmente cada registro.",
        iconAlt: "Escudo con verificación",
      },
      {
        key: "privacy" as const,
        title: "Privacidad de tus datos",
        body: "Tu información como usuario no se comparte ni se vende. El profesional solo ve lo que vos decidís enviarle al contactarlo.",
        iconAlt: "Candado cerrado",
      },
      {
        key: "boundary" as const,
        title: "Sin intermediación clínica",
        body: "Lo que pasa entre profesional y cliente queda entre ellos. La plataforma no participa del encuentro ni guarda registros privados.",
        iconAlt: "Dos figuras separadas por una línea",
      },
    ],
  },
  showApp: {
    eyebrow: "Galería del producto",
    title: "Así se ve por dentro",
    subtitle:
      "Conocé la app antes de descargarla. Una experiencia pensada para que encuentres rápido, elijas con criterio y contactes sin trabas.",
    swipeHint: "Deslizá para ver más",
    home: {
      locationLabel: "Ubicación",
      locationValue: "General Manuel Belgrano 12",
      avatarInitials: "JA",
      categories: [
        { key: "psicologia" as const, label: "Psicología", icon: "brain" as const },
        { key: "nutricion" as const, label: "Nutrición", icon: "apple" as const },
        { key: "odontologia" as const, label: "Odontología", icon: "tooth" as const },
      ],
      tabBar: {
        home: "Inicio",
        profile: "Perfil",
      },
    },
    profile: {
      category: "Psicología",
      nuevoLabel: "Nuevo",
      locationTitle: "Ubicación",
      mapAttribution: "Maps  ·  Plaza Independencia, Mendoza",
      modalityTitle: "Modalidad de trabajo",
      modalityOnline: "Trabajo online",
      modalityPresencial: "Trabajo presencial",
    },
    screens: [
      {
        key: "home" as const,
        title: "Inicio",
        caption: "Profesionales en tu zona, ordenados por distancia.",
        alt: "Pantalla de inicio con lista de profesionales cercanos",
      },
      {
        key: "profile" as const,
        title: "Perfil profesional",
        caption: "Toda la información que necesitás para elegir.",
        alt: "Pantalla de perfil de un profesional con biografía y áreas",
      },
      {
        key: "search" as const,
        title: "Filtros",
        caption: "Filtrá por las obras sociales que aceptás.",
        alt: "Pantalla de filtros por obra social",
      },
    ],
    categories: [
      "Ansiedad",
      "Pareja",
      "Adolescentes",
      "Duelo",
      "Adicciones",
      "Autoestima",
    ],
    filters: {
      headerTitle: "Psicología",
      filtersLabel: "Filtros",
      selectedCount: 1,
      sheetTitle: "Filtros",
      sectionLabel: "Obras sociales",
      sectionHelp:
        "Mostrar solo profesionales que aceptan alguna de estas obras sociales",
      selected: ["OSDE"],
      groups: [
        {
          label: "Prepagas y obras sociales populares",
          items: [
            "OSDE", "PAMI", "OSEP", "Swiss Medical", "OSECAC",
            "Galeno", "Medicus", "Omint", "OSDEPYM",
          ],
        },
        {
          label: "Prepagas",
          items: [
            "SanCor Salud", "Hospital Italiano", "Hospital Alemán",
            "Hospital Británico", "Medifé", "Avalian", "Federada Salud",
            "Prevención Salud", "Premedic", "Vittal", "William Hope",
            "Boreal Salud", "TIM",
          ],
        },
        {
          label: "Obras sociales nacionales",
          items: [
            "OSPRERA", "OSPACA", "UOM / OSPIM", "UOCRA", "OSPe",
            "OSUTHGRA", "OSPSA", "Bancaria",
          ],
        },
      ],
      clear: "Limpiar",
      apply: "Aplicar",
    },
  },
  about: {
    eyebrow: "Quiénes somos",
    title: "Las personas detrás de Profind",
    intro:
      "Construimos Profind porque pasamos por la búsqueda y supimos que tenía que existir una forma mejor. Somos dos en Mendoza, trabajando todos los días para que encontrar un profesional deje de ser un proceso a ciegas.",
    // TODO: confirmar apellido de Mauro, sumar fotos profesionales y URLs de LinkedIn reales.
    members: [
      {
        name: "Jaled Jara",
        role: "Co-fundador & CTO",
        blurb:
          "Construyo el producto. Mi obsesión es que la búsqueda no se interponga entre la persona y el profesional.",
        initials: "JJ",
        photoSrc: undefined as string | undefined,
        linkedinUrl: undefined as string | undefined,
      },
      {
        name: "Mauro Acosta",
        role: "Co-fundador & CEO",
        blurb:
          "Vi de cerca lo difícil que es dar con el profesional indicado. Profind es la respuesta que me hubiera gustado tener.",
        initials: "MA",
        photoSrc: undefined as string | undefined,
        linkedinUrl: undefined as string | undefined,
      },
    ],
  },
  faq: {
    eyebrow: "Preguntas frecuentes",
    title: "Las dudas más comunes",
    subtitle:
      "Lo que solemos contestar por mensaje. Cada pregunta lleva una etiqueta — escaneá solo las que te tocan.",
    audienceLabel: {
      user: "Usuario",
      professional: "Profesional",
    },
    items: [
      {
        audience: "user" as const,
        question: "¿Cómo verifican que los profesionales son reales?",
        answer:
          "Validamos manualmente la matrícula de cada profesional contra el registro oficial antes de habilitar su perfil. Si alguien no aparece, es porque todavía no completó la verificación.",
      },
      {
        audience: "user" as const,
        question: "¿En qué ciudades está disponible?",
        answer:
          "Por ahora, Mendoza, Argentina. Estamos empezando acá y vamos a ir sumando ciudades a medida que crezca la red de profesionales.",
      },
      {
        audience: "user" as const,
        question: "¿Puedo encontrar profesionales que atiendan online?",
        answer:
          "Sí. Filtrás por modalidad presencial, online o ambas. Si elegís online, podés contactar a cualquier profesional de Argentina, no solo a los de tu zona.",
      },
      {
        audience: "user" as const,
        question: "¿Atienden por obra social o prepaga?",
        answer:
          "Cada profesional indica si trabaja con obras sociales o prepagas, y cuáles acepta. Filtrás por la tuya y solo ves a quienes la toman.",
      },
      {
        audience: "professional" as const,
        question: "¿Cuánto cuesta para el profesional?",
        answer:
          "Hoy nada: el plan base es gratis y te deja siempre visible, con tu panel de visitas y clics incluido. Más adelante habrá un plan premium opcional con funciones extra — el plan base va a seguir disponible. Sin comisiones por cliente: lo que pase entre vos y tu cliente queda entre ustedes.",
      },
      {
        audience: "professional" as const,
        question: "¿El plan base tiene vencimiento?",
        answer:
          "No. El plan base es gratis y sin límite de tiempo. Cuando lancemos el plan premium opcional, vas a poder seguir con el base o pasarte si te suma — sin permanencia ni trámites.",
      },
      {
        audience: "professional" as const,
        question: "¿Los clientes me contactan por teléfono?",
        answer:
          "Por los canales que vos elijas mostrar en tu perfil: WhatsApp, Instagram, LinkedIn, X o tu sitio web. El usuario coordina directo con vos, sin intermediarios ni vista nuestra de la conversación.",
      },
      {
        audience: "professional" as const,
        question: "¿Cómo verifican mi matrícula?",
        answer:
          "Te pedimos número de matrícula y colegio profesional al registrarte. Validamos contra el registro oficial; el proceso suele demorar entre 24 y 48 horas hábiles.",
      },
      {
        audience: "professional" as const,
        question: "¿Cómo me doy de baja?",
        answer:
          "Desde tu perfil, en un click. La baja es inmediata: dejás de aparecer en los resultados y no se te vuelve a cobrar. Sin llamadas, sin retención.",
      },
    ],
  },
  finalCta: {
    title: "Encontrá a tu profesional hoy.",
    subtitle: "Gratis. Sin registro previo para explorar.",
    storeKicker: "Descargá en",
    storeApple: "App Store",
    storeGoogle: "Google Play",
    storeAppleAria: "Descargar Profind en la App Store",
    // Android todavía no está publicada en Play Store: el botón no linkea a la
    // store (evita el "app no encontrada") — avisa que está en camino al tocarlo.
    storeGoogleKicker: "Próximamente en",
    storeGoogleBadge: "Pronto",
    storeGoogleAria:
      "Google Play, próximamente. Estamos trabajando en la versión para Android.",
    storeGoogleNote: "Estamos trabajando en la versión para Android. ¡Muy pronto!",
    secondary: "¿Sos profesional? Sumate",
  },
  footer: {
    pitch:
      "Conectamos profesionales con personas que los buscan.",
    columnTitles: {
      product: "Producto",
      legal: "Legal",
    },
    product: [
      { label: "¿Qué es?", href: "#que-es" },
      { label: "Cómo funciona", href: "#como-funciona" },
      { label: "Para profesionales", href: "#para-profesionales" },
      { label: "FAQ", href: "#faq" },
    ],
    legal: [
      { label: "Términos y condiciones", href: "/terms" },
      { label: "Política de privacidad", href: "/privacy" },
      { label: "Soporte al cliente", href: "/soporte" },
    ],
    social: {
      groupLabel: "Redes sociales",
      items: [
        { key: "instagram" as const, label: "Instagram", href: "#" },
        { key: "linkedin" as const, label: "LinkedIn", href: "#" },
      ],
    },
    copyright: "© 2026 Profind. Todos los derechos reservados.",
    madeIn: "Hecho en Argentina",
    brandAriaHome: "Profind — volver al inicio",
  },
  support: {
    title: "Soporte y contacto",
    subtitle:
      "¿Tenés una duda, un problema con la app o querés darnos una mano? Escribinos por el canal que prefieras. Somos un equipo chico y leemos todo.",
    channelsTitle: "Cómo contactarnos",
    channels: {
      whatsapp: {
        label: "WhatsApp",
        description: "Lo más rápido. Escribinos por chat.",
        action: "Abrir WhatsApp",
      },
      phone: {
        label: "Teléfono",
        description: "Si preferís llamar, marcá el mismo número.",
        action: "Llamar",
      },
      email: {
        label: "Email",
        description: "Para consultas más largas o adjuntar info.",
        action: "Enviar email",
      },
    },
    responseTime: "Respondemos dentro de las 48 horas hábiles.",
    deletion: {
      title: "Eliminar tu cuenta de Profind",
      intro:
        "Podés eliminar tu cuenta de Profind y todos tus datos personales cuando quieras. El proceso es permanente e irreversible.",
      optionsTitle: "Tenés dos formas de hacerlo:",
      options: [
        "Desde la app: entrá a tu Perfil → Más opciones → Eliminar cuenta y seguí los pasos. La baja es inmediata.",
        "Escribinos: mandanos un mensaje por WhatsApp o un email desde la dirección asociada a tu cuenta y la damos de baja por vos. La procesamos manualmente.",
      ],
      whatTitle: "Qué se elimina",
      whatItems: [
        "Tu perfil y datos personales",
        "Tu ubicación registrada",
        "Tus reseñas y calificaciones",
        "Tus notificaciones",
      ],
      whatConservedTitle: "Qué se conserva",
      whatConservedIntro:
        "Estos registros no se eliminan, se anonimizan: dejan de estar asociados a tu cuenta, pero el dato agregado se mantiene.",
      whatConservedItems: [
        "Las visitas a perfiles profesionales y los clics en redes sociales que generaste como visitante: el profesional sigue viendo el conteo en sus estadísticas, pero deja de poder vincularlo con vos.",
        "El historial de tus búsquedas en la app, que usamos internamente para estadísticas de uso, también se desvincula de tu cuenta en vez de borrarse.",
      ],
      timeline:
        "Los datos se eliminan de forma permanente a los 30 días de confirmada la solicitud. La anonimización de lo que se conserva ocurre dentro del mismo plazo.",
      privacyPrompt: "Para más detalle sobre cómo tratamos tus datos, leé nuestra",
      privacyLinkText: "Política de Privacidad",
      privacyHref: "/privacy",
    },
  },
  legal: {
    updatedPrefix: "Última actualización:",
    months: [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
    ],
    roleClient: "Para Usuarios",
    roleProfessional: "Para Profesionales",
    terms: { title: "Términos y Condiciones de Uso" },
    privacy: { title: "Política de Privacidad" },
  },
  placeholder: {
    nextSession: "Contenido en próxima sesión",
  },
} as const;
